// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  tickerTagEntityAdapter,
  tagManagerEntityAdapter,
} from '@shared/data-access/slices/tag-manager';
import { isUndefined } from '@shared/utils';
import type {
  SearchTickerTag,
  TickerTag,
  TickerTagFeature,
} from '@shared/data-access/interfaces/api/engine/citec';
import { engineApi } from '@shared/data-access/api-base/citec-engine-api';
import { groupBy } from '@shared/data-access/utils/groupby';
import { tagManagerSlice } from '@shared/data-access/slices/tag-manager';
import {
  type Tag,
  type TickerTagState,
} from '@shared/data-access/interfaces/redux/tag-manager-state';
import { type EntityState } from '@reduxjs/toolkit';

export const tickerTagApi = engineApi.injectEndpoints({
  endpoints: build => ({
    searchTickerTag: build.query<
      EntityState<TickerTagState, string>,
      SearchTickerTag
    >({
      query: params => ({
        url: '/citec/tags/search/',
        method: 'GET',
        params,
      }),
      transformResponse: (response: TickerTag[]) => {
        /* istanbul ignore next */
        const groupedByFeature = groupBy(response, 'feature');
        /* istanbul ignore next */
        const adaptedEntities = Array.from(groupedByFeature.entries()).reduce(
          (acc, [feature, tickerTags]) => {
            const groupedByTag = groupBy(tickerTags, 'tag');
            const tags = Array.from(groupedByTag.entries()).map(
              ([tagName, tagData]) => ({
                name: tagName,
                tickers: tagData.flatMap(data => data.tickers),
              }),
            );

            return {
              ...acc,
              [feature]: {
                feature: feature as TickerTagFeature,
                selectedTag: '',
                tags: tickerTagEntityAdapter.setAll(
                  tickerTagEntityAdapter.getInitialState(),
                  tags,
                ),
              },
            };
          },
          {} as Record<TickerTagFeature, TickerTagState>,
        );
        /* istanbul ignore next */
        return tagManagerEntityAdapter.setAll(
          tagManagerEntityAdapter.getInitialState(),
          Object.values(adaptedEntities),
        );
      },
      /* istanbul ignore next */
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          Object.entries(data.entities).forEach(([feature, featureData]) => {
            const tags = featureData
              ? Object.values(featureData.tags.entities)
              : [];
            if (tags.length > 0) {
              dispatch(
                tagManagerSlice.actions.setTags([
                  feature as TickerTagFeature,
                  tags.filter((tag): tag is Tag => !isUndefined(tag)),
                ]),
              );
            }
          });
        } catch (error) {
          console.error('[searchTickerTag]', error);
        }
      },
      providesTags: /* istanbul ignore next */ (result, error, arg) => [
        { type: 'TickerTag', id: arg.feature },
      ],
    }),

    createTickerTag: build.mutation<TickerTag[], { payload: TickerTag[] }>({
      query: ({ payload }) => ({
        url: '/citec/tags/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TickerTag', id: arg.payload[0].feature },
      ],
    }),

    deleteTickerTag: build.mutation<
      void,
      { feature: TickerTagFeature; tags?: string; tickers?: string }
    >({
      query: ({ feature, tags, tickers }) => ({
        url: `/citec/tags/${feature}/`,
        method: 'DELETE',
        params: { tags, tickers },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TickerTag', id: arg.feature },
      ],
    }),
  }),
});
